import { DateTime } from "luxon";

export default function useDateFormatter() {
  /**
   * Formats a date string or Date object into a localized French format
   * @param {string|Date} dateInput - Date string or JavaScript Date object
   * @param {boolean} includeTime - Whether to include time in the output
   * @returns {string|null} - Formatted date string or null if invalid
   */
  function formatDate(
    dateInput,
    includeTime = false,
    format = DateTime.DATE_FULL,
    zone = "Europe/Paris",
  ) {
    if (!dateInput) return null;
    if (includeTime) {
      format = DateTime.DATETIME_SHORT;
    }

    // Handle JavaScript Date object input
    if (dateInput instanceof Date) {
      return DateTime.fromJSDate(dateInput)
        .setLocale("fr-FR")
        .setZone(zone)
        .toLocaleString(format);
    }

    // Handle string inputs
    if (typeof dateInput === "string") {
      // Check if already in French format
      if (
        DateTime.fromFormat(dateInput, "dd/MM/yyyy", { locale: "fr-FR" })
          .isValid ||
        DateTime.fromFormat(dateInput, "dd/MM/yyyy HH:mm", { locale: "fr-FR" })
          .isValid
      ) {
        return dateInput;
      }

      // For dates with microseconds, first trim the microseconds to milliseconds
      // or remove them entirely before parsing
      let processedInput = dateInput;
      if (dateInput.includes(".") && dateInput.split(".")[1].length > 3) {
        // Extract date part and milliseconds (first 3 digits after the decimal)
        const dateParts = dateInput.split(".");
        const milliseconds = dateParts[1].substring(0, 3);
        processedInput = `${dateParts[0]}.${milliseconds}`;
      }

      // Try custom format with milliseconds (max 3 digits that Luxon supports)
      const dateTimeFromCustomWithMilli = DateTime.fromFormat(
        processedInput,
        "yyyy-MM-dd HH:mm:ss.SSS",
      );

      if (dateTimeFromCustomWithMilli.isValid) {
        return dateTimeFromCustomWithMilli
          .setLocale("fr-FR")
          .setZone(zone)
          .toLocaleString(format);
      }

      // Try custom format without milliseconds
      const dateTimeFromCustom = DateTime.fromFormat(
        dateInput,
        "yyyy-MM-dd HH:mm:ss",
      );

      if (dateTimeFromCustom.isValid) {
        return dateTimeFromCustom
          .setLocale("fr-FR")
          .setZone(zone)
          .toLocaleString(format);
      }

      // Try custom format with colons as separators
      const dateTimeFromCustomColons = DateTime.fromFormat(
        dateInput,
        "yyyy:MM:dd HH:mm:ss",
      );

      if (dateTimeFromCustomColons.isValid) {
        return dateTimeFromCustomColons
          .setLocale("fr-FR")
          .setZone(zone)
          .toLocaleString(format);
      }

      // Try ISO format
      const dateTimeFromIso = DateTime.fromISO(dateInput, { setZone: true });

      if (dateTimeFromIso.isValid) {
        return dateTimeFromIso
          .setLocale("fr-FR")
          .setZone(zone)
          .toLocaleString(format);
      }
    }
    return null;
  }

  /**
   * Formats an ISO date string to yyyy/MM/dd format
   * @param {string} dateString - ISO date string
   * @returns {string|null} - Formatted date or null if invalid
   */
  function formatISODate(dateString, includeTime = false) {
    const dt = DateTime.fromISO(dateString);
    if (includeTime) {
      return dt.isValid ? dt.toFormat("yyyy-MM-dd HH:mm:ss") : null;
    }
    return dt.isValid ? dt.toFormat("yyyy-MM-dd") : null;
  }

  /**
   * Parses a date string or Date object to JavaScript Date object
   * @param {string|Date} dateInput - Input to be converted to Date (same type as formatDate)
   * @returns {Date|null} - JavaScript Date object or null if parsing fails
   */
  function parseToDate(dateInput, zone = "Europe/Paris") {
    if (!dateInput) return null;

    // Already a Date object
    if (dateInput instanceof Date) {
      return DateTime.fromJSDate(dateInput)
        .setZone(zone)
        .toJSDate();
    }

    // Handle string inputs
    if (typeof dateInput === "string") {
      // French format (dd/MM/yyyy)
      const frenchDate = DateTime.fromFormat(dateInput, "dd/MM/yyyy", {
        locale: "fr-FR",
      });
      if (frenchDate.isValid) {
        return frenchDate.toJSDate();
      }

      // French format with time (dd/MM/yyyy HH:mm)
      const frenchDateTime = DateTime.fromFormat(
        dateInput,
        "dd/MM/yyyy HH:mm",
        {
          locale: "fr-FR",
        },
      );
      if (frenchDateTime.isValid) {
        return frenchDateTime.toJSDate();
      }

      // Process microseconds for custom formats
      let processedInput = dateInput;
      if (dateInput.includes(".") && dateInput.split(".")[1].length > 3) {
        const dateParts = dateInput.split(".");
        const milliseconds = dateParts[1].substring(0, 3);
        processedInput = `${dateParts[0]}.${milliseconds}`;
      }

      // Custom format with milliseconds
      const dateWithMs = DateTime.fromFormat(
        processedInput,
        "yyyy-MM-dd HH:mm:ss.SSS",
      );
      if (dateWithMs.isValid) {
        return dateWithMs.toJSDate();
      }

      // Custom format without milliseconds
      const dateWithoutMs = DateTime.fromFormat(
        dateInput,
        "yyyy-MM-dd HH:mm:ss",
      );
      if (dateWithoutMs.isValid) {
        return dateWithoutMs.toJSDate();
      }

      // Custom format with colons as separators
      const dateWithColons = DateTime.fromFormat(
        dateInput,
        "yyyy:MM:dd HH:mm:ss"
      );
      if (dateWithColons.isValid) {
        return dateWithColons.toJSDate();
      }

      // ISO format (with timezone handling)
      const isoDate = DateTime.fromISO(dateInput, { setZone: true, zone });
      if (isoDate.isValid) {
        return isoDate.toJSDate();
      }
    }

    return null;
  }

  /**
   * Parses a date string or Date object to luxon DateTime object
   * @param {string|Date} dateInput - Input to be converted to Date (same type as formatDate)
   * @returns {DateTime|null} - luxon DateTime object or null if parsing fails
   */
  function parseToDateTime(dateInput) {
    if (!dateInput) return null;

    // Already a Date object
    if (dateInput instanceof Date) {
      return DateTime.fromJSDate(dateInput);
    }

    // Handle string inputs
    if (typeof dateInput === "string") {
      // French format (dd/MM/yyyy)
      const frenchDate = DateTime.fromFormat(dateInput, "dd/MM/yyyy", {
        locale: "fr-FR",
      });
      if (frenchDate.isValid) {
        return frenchDate;
      }

      // French format with time (dd/MM/yyyy HH:mm)
      const frenchDateTime = DateTime.fromFormat(
        dateInput,
        "dd/MM/yyyy HH:mm",
        {
          locale: "fr-FR",
        },
      );
      if (frenchDateTime.isValid) {
        return frenchDateTime;
      }

      // Process microseconds for custom formats
      let processedInput = dateInput;
      if (dateInput.includes(".") && dateInput.split(".")[1].length > 3) {
        const dateParts = dateInput.split(".");
        const milliseconds = dateParts[1].substring(0, 3);
        processedInput = `${dateParts[0]}.${milliseconds}`;
      }

      // Custom format with milliseconds
      const dateWithMs = DateTime.fromFormat(
        processedInput,
        "yyyy-MM-dd HH:mm:ss.SSS",
      );
      if (dateWithMs.isValid) {
        return dateWithMs;
      }

      // Custom format without milliseconds
      const dateWithoutMs = DateTime.fromFormat(
        dateInput,
        "yyyy-MM-dd HH:mm:ss",
      );
      if (dateWithoutMs.isValid) {
        return dateWithoutMs;
      }

      // Custom format with colons as separators
      const dateWithColons = DateTime.fromFormat(
        dateInput,
        "yyyy:MM:dd HH:mm:ss"
      );
      if (dateWithColons.isValid) {
        return dateWithColons;
      }

      // ISO format (with timezone handling)
      const isoDate = DateTime.fromISO(dateInput, { setZone: true });
      if (isoDate.isValid) {
        return isoDate;
      }
    }

    return null;
  }

  /**
   *
   * Formats a time value to HH:mm:ss format
   * @param {string|Date|null|undefined} timeValue - Time value (can be HH:mm:ss string, Date object, or full datetime ISO string)
   * @returns {string} - Formatted time string in HH:mm:ss format
   */
  function formatTime(timeValue) {
    try {
      // Handle null/undefined
      if (timeValue == null) {
        return "";
      }

      // Handle Date objects directly
      if (timeValue instanceof Date) {
        return DateTime.fromJSDate(timeValue).toFormat("HH:mm:ss");
      }

      // Convert to string for string operations
      const timeStr = String(timeValue);

      // If it's already a time-only string (HH:mm:ss format), return as is
      if (/^\d{2}:\d{2}:\d{2}$/.test(timeStr)) {
        return timeStr;
      }

      // If it's a full datetime string, extract just the time part
      if (timeStr.includes("T")) {
        return DateTime.fromISO(timeStr).toUTC().toFormat("HH:mm:ss");
      }

      // If it's a JavaScript Date string format (e.g., "Thu Sep 25 2025 16:59:07 GMT+0200")
      if (timeStr.includes("GMT")) {
        const jsDate = new Date(timeStr);
        const luxonDateTime = DateTime.fromJSDate(jsDate);
        if (luxonDateTime.isValid) {
          return luxonDateTime.toFormat("HH:mm:ss");
        }
      }

      // Try to parse as time and format
      return DateTime.fromISO(timeStr).toFormat("HH:mm:ss");
    } catch (e) {
      console.log("Error formatting time", timeValue, e);
      return timeValue;
    }
  }
  return {
    parseToDate,
    parseToDateTime,
    formatDate,
    formatISODate,
    formatTime,
  };
}
